import {
  type KaraokeClip,
  MINIMUM_KARAOKE_CLIP_DURATION,
} from '../../models/karaoke/KaraokeClip';
import { AbstractCommand } from '../AbstractCommand';

import type { VideoEditor } from '../../VideoEditor';

export class AdjustKaraokeClipTimeCommand extends AbstractCommand {
  private karaokeClipId: string;
  private offset: { startSeconds?: number; endSeconds?: number };
  private previousOffsets: {
    startSecondsOffset?: number;
    endSecondsOffset?: number;
  } | null = null;
  private autoCorrect: boolean;

  constructor(
    editor: VideoEditor,
    karaokeClipId: string,
    offset: { startSeconds?: number; endSeconds?: number },
    autoCorrect = false,
  ) {
    super(editor);
    this.karaokeClipId = karaokeClipId;
    this.offset = offset;
    this.autoCorrect = autoCorrect;
  }

  execute(): boolean {
    const track = this.editor.getKaraokeTrack();
    if (!track) return false;

    const clipIndex = track.karaokeClips.findIndex(
      clip => clip.id === this.karaokeClipId,
    );
    if (clipIndex === -1) return false;

    const clip = track.karaokeClips[clipIndex];

    // Store previous offsets for undo
    this.previousOffsets = {
      startSecondsOffset: clip.startSecondsOffset,
      endSecondsOffset: clip.endSecondsOffset,
    };

    // Calculate new offsets
    let newStartOffset =
      this.offset.startSeconds !== undefined
        ? this.offset.startSeconds
        : clip.startSecondsOffset || 0;
    let newEndOffset =
      this.offset.endSeconds !== undefined
        ? this.offset.endSeconds
        : clip.endSecondsOffset || 0;

    // Validate and optionally autocorrect the change
    const validation = this.validateAndCorrectOffsets(
      clip,
      newStartOffset,
      newEndOffset,
      clipIndex,
      this.autoCorrect,
    );

    if (!validation.valid) {
      return false;
    }

    newStartOffset = validation.startOffset;
    newEndOffset = validation.endOffset;

    // Apply the change
    clip.startSecondsOffset = newStartOffset;
    clip.endSecondsOffset = newEndOffset;

    this.editor.markDirty('adjust_karaoke_clip_time');
    return true;
  }

  undo(): boolean {
    if (!this.previousOffsets) return false;

    const track = this.editor.getKaraokeTrack();
    if (!track) return false;

    const clip = track.karaokeClips.find(c => c.id === this.karaokeClipId);
    if (!clip) return false;

    clip.startSecondsOffset = this.previousOffsets.startSecondsOffset;
    clip.endSecondsOffset = this.previousOffsets.endSecondsOffset;

    this.editor.markDirty('adjust_karaoke_clip_time_undo');
    return true;
  }

  getName(): string {
    return 'AdjustKaraokeClipCommand';
  }

  getDescription(): string {
    return 'Adjusts karaoke clip time offsets';
  }

  /**
   * Validates and optionally autocorrects offsets to resolve overlaps
   * Returns validation result with potentially corrected offsets
   */
  private validateAndCorrectOffsets(
    clip: KaraokeClip,
    requestedStartOffset: number,
    requestedEndOffset: number,
    clipIndex: number,
    autoCorrect: boolean,
  ): {
    valid: boolean;
    startOffset: number;
    endOffset: number;
  } {
    const track = this.editor.getKaraokeTrack();
    if (!track) {
      return {
        valid: false,
        startOffset: requestedStartOffset,
        endOffset: requestedEndOffset,
      };
    }

    const transcriptStart = clip.getTranscriptStartTime();
    const transcriptEnd = clip.getTranscriptEndTime();

    if (transcriptStart === null || transcriptEnd === null) {
      console.error('Cannot adjust clip: invalid transcript times');
      return {
        valid: false,
        startOffset: requestedStartOffset,
        endOffset: requestedEndOffset,
      };
    }

    let correctedStartOffset = requestedStartOffset;
    let correctedEndOffset = requestedEndOffset;

    // Check overlap with previous clip
    if (clipIndex > 0) {
      const prevClip = track.karaokeClips[clipIndex - 1];
      const prevEnd = prevClip.getKaraokeEndTime();

      if (prevEnd !== null) {
        const requestedStart = transcriptStart + correctedStartOffset;
        if (requestedStart < prevEnd) {
          if (autoCorrect) {
            correctedStartOffset = prevEnd - transcriptStart;
            console.log(
              `Autocorrected clip ${clip.id} startOffset from ${requestedStartOffset} to ${correctedStartOffset} to avoid overlap with previous clip`,
            );
          } else {
            console.error(
              `Cannot adjust clip: would overlap with previous clip (new start ${requestedStart}s < previous end ${prevEnd}s)`,
            );
            return {
              valid: false,
              startOffset: requestedStartOffset,
              endOffset: requestedEndOffset,
            };
          }
        }
      }
    }

    // Check overlap with next clip
    if (clipIndex < track.karaokeClips.length - 1) {
      const nextClip = track.karaokeClips[clipIndex + 1];
      const nextStart = nextClip.getKaraokeStartTime();

      if (nextStart !== null) {
        const requestedEnd = transcriptEnd + correctedEndOffset;
        if (requestedEnd > nextStart) {
          if (autoCorrect) {
            correctedEndOffset = nextStart - transcriptEnd;
            console.log(
              `Autocorrected clip ${clip.id} endOffset from ${requestedEndOffset} to ${correctedEndOffset} to avoid overlap with next clip`,
            );
          } else {
            console.error(
              `Cannot adjust clip: would overlap with next clip (new end ${requestedEnd}s > next start ${nextStart}s)`,
            );
            return {
              valid: false,
              startOffset: requestedStartOffset,
              endOffset: requestedEndOffset,
            };
          }
        }
      }
    }

    // Check minimum duration
    const finalStart = transcriptStart + correctedStartOffset;
    const finalEnd = transcriptEnd + correctedEndOffset;
    const finalDuration = finalEnd - finalStart;

    if (finalDuration < MINIMUM_KARAOKE_CLIP_DURATION) {
      const message = autoCorrect
        ? `Autocorrect failed: corrected duration ${finalDuration}s is less than minimum ${MINIMUM_KARAOKE_CLIP_DURATION}s`
        : `Cannot adjust clip: duration ${finalDuration}s would be less than minimum ${MINIMUM_KARAOKE_CLIP_DURATION}s`;
      console.error(message);
      return {
        valid: false,
        startOffset: requestedStartOffset,
        endOffset: requestedEndOffset,
      };
    }

    return {
      valid: true,
      startOffset: correctedStartOffset,
      endOffset: correctedEndOffset,
    };
  }
}
